import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { adminTireService, type TireFilters, type AdminTireUpdateData } from '@/services/api/adminTireService';
import type { Tire } from '@/types/tire';
import { adminCustomerService } from '@/services/api/adminCustomerService';
import type { Customer } from '@/types/customer';

interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  pending_count: number;
  approved_count: number;
  rejected_count: number;
}

export const useAdminTireStore = defineStore('adminTire', () => {
  const tires = ref<Tire[]>([]);
  const selectedTire = ref<Tire | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  
  const filters = ref<TireFilters>({
    search: '',
    status: '',
    brand: undefined,
    page: 1,
    per_page: 10
  });

  const pagination = ref<PaginationMeta>({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
    pending_count: 0,
    approved_count: 0,
    rejected_count: 0,
  });

  const pendingCount = computed(() => pagination.value.pending_count);
  const approvedCount = computed(() => pagination.value.approved_count);
  const rejectedCount = computed(() => pagination.value.rejected_count);

  async function fetchTires() {
    loading.value = true;
    error.value = null;

    try {
      const response = await adminTireService.getTires(filters.value);
      tires.value = response.data;
      pagination.value = response.meta;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch tires';
      console.error('Error fetching tires:', err);
    } finally {
      loading.value = false;
    }
  }

  async function approveTire(tireId: number) {
    loading.value = true;
    error.value = null;

    try {
      await adminTireService.approveTire(tireId);
      
      await fetchTires();
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to approve tire';
      console.error('Error approving tire:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function rejectTire(tireId: number, rejectionReason?: string) {
    loading.value = true;
    error.value = null;

    try {
      await adminTireService.rejectTire(tireId, {
        rejection_reason: rejectionReason
      });

      await fetchTires();
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to reject tire';
      console.error('Error rejecting tire:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateTire(
    tireId: number,
    data: AdminTireUpdateData,
    newTirePhotos?: File[],
    deletedTirePhotoIds?: number[],
    newReceiptPhotos?: File[],
    deletedReceiptPhotoIds?: number[]
  ) {
    loading.value = true;
    error.value = null;

    try {
      await adminTireService.updateTire(tireId, data, newTirePhotos, deletedTirePhotoIds, newReceiptPhotos, deletedReceiptPhotoIds);
      await fetchTires();
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update tire';
      console.error('Error updating tire:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deleteTire(tireId: number) {
    loading.value = true;
    error.value = null;

    try {
      await adminTireService.deleteTire(tireId);
      await fetchTires();
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to delete tire';
      console.error('Error deleting tire:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  function setFilters(newFilters: Partial<TireFilters>) {
    filters.value = { ...filters.value, ...newFilters };

    if (!filters.value.per_page || filters.value.per_page <= 0) {
      filters.value.per_page = 10;
    }

    if (!filters.value.page || filters.value.page <= 0) {
      filters.value.page = 1;
    }
  }

  function resetFilters() {
    filters.value = {
      search: '',
      status: '',
      brand: undefined,
      page: 1,
      per_page: 10
    };
  }

  function clearError() {
    error.value = null;
  }

  async function exportTires() {
    loading.value = true;
    error.value = null;

    try {
      // Import XLSX dynamically
      const XLSX = await import('xlsx');

      // Fetch ALL filtered tires (no pagination)
      const allTires = await adminTireService.getTiresForExport(filters.value);

      // Brand prefix mapping
      const BRAND_PREFIXES: Record<string, { customer: string; tire: string }> = {
        'Continental': { customer: 'TCP-CC', tire: 'TCP-TR' },
        'Falken':      { customer: 'RHR-CC', tire: 'RHR-TR' },
      };
      const getBrandPrefix = (brandName: string) =>
        BRAND_PREFIXES[brandName] ?? { customer: 'UNK-CC', tire: 'UNK-TR' };

      // Per-brand row counter — shared across tire rows and no-tire rows
      const brandCounters: Record<string, number> = {};

      // Photo URL helper
      const baseUrl = (process.env.VUE_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');
      const toPhotoUrl = (path: string) => `${baseUrl}/storage/${path}`;

      // Build unified 41-column rows from tire data
      const tireRows: Record<string, string | number>[] = allTires.map(tire => {
        const brandName = tire.brand?.brand_name || 'Unknown';
        brandCounters[brandName] = (brandCounters[brandName] || 0) + 1;
        const rowNo = brandCounters[brandName];

        const detail = tire.user?.userDetail ?? tire.user?.user_detail ?? null;
        const address = [detail?.house_no, detail?.street, detail?.barangay, detail?.city]
          .filter(Boolean)
          .join(', ');

        const claimPhotos = tire.claim?.claim_photos ?? [];
        const wholeTirePhotos = claimPhotos
          .filter(p => p.media_type === 'whole_tire')
          .map(p => toPhotoUrl(p.photo_path))
          .join(', ');
        const treadViewPhotos = claimPhotos
          .filter(p => p.media_type === 'tread_view')
          .map(p => toPhotoUrl(p.photo_path))
          .join(', ');
        const damageVideos = claimPhotos
          .filter(p => p.media_type === 'damage_video')
          .map(p => toPhotoUrl(p.photo_path))
          .join(', ');

        return {
          'No.':                         rowNo,
          'Category':                    tire.user?.category === 'internal' ? 'Internal' : 'Customer',
          'Customer Code':               `${getBrandPrefix(brandName).customer}${tire.user?.user_id ?? ''}`,
          'Customer Name':               tire.user ? `${tire.user.first_name} ${tire.user.last_name}`.trim() : '',
          'Email':                       tire.user?.email ?? '',
          'Mobile':                      detail?.mobile ?? '',
          'Brand':                       brandName,
          'Address':                     address,
          'Status (Customer)':           tire.user?.status ?? '',
          'Vehicles Count':              tire.user?.vehicles_count ?? '',
          'Tires Count':                 tire.user?.tires_count ?? '',
          'Date Joined':                 tire.user?.created_at ? new Date(tire.user.created_at).toLocaleDateString() : '',
          'Tire Registration Code':      `${getBrandPrefix(brandName).tire}${tire.tire_id}`,
          'Pattern':                     tire.pattern ?? '',
          'Tire Size':                   tire.tire_size ?? '',
          'Tire Position':               tire.tire_position ?? '',
          'DOT':                         tire.dot ?? '',
          'Tire Photos':                 tire.tire_photos?.map(p => toPhotoUrl(p.photo_path)).join(', ') ?? '',
          'Receipt Photos':              tire.receipt_photos?.map(p => toPhotoUrl(p.photo_path)).join(', ') ?? '',
          'Shop':                        tire.shop ?? '',
          'Code':                        tire.code ?? '',
          'Invoice Number':              tire.invoice_number ?? '',
          'Purchase Date':               tire.purchase_date ?? '',
          'Vehicle':                     tire.vehicle ? `${tire.vehicle.vehicle_make} ${tire.vehicle.vehicle_model}`.trim() : '',
          'Vehicle Year':                tire.vehicle?.vehicle_year ?? '',
          'Plate Number':                tire.vehicle?.plate_number ?? '',
          'Approval Status':             tire.approval_status ?? '',
          'Rejection Reason (Tire)':     tire.rejection_reason ?? '',
          'Date Submitted (Tire)':       tire.created_at ? new Date(tire.created_at).toLocaleDateString() : '',
          'Claim Number':                tire.claim?.claim_number ?? '',
          'Dealer (Replacement)':        tire.claim?.dealer?.dealer_name ?? '',
          'Dealer Address':              tire.claim?.dealer?.dealer_address ?? '',
          'Date Submitted (Claim)':      tire.claim?.created_at ? new Date(tire.claim.created_at).toLocaleDateString() : '',
          'Status (Claim)':              tire.claim?.claim_status ?? '',
          'Video Call Schedule':         tire.claim?.videocall_schedule ?? '',
          'Damage Photos (Whole Tire)':  wholeTirePhotos,
          'Damage Photos (Tread View)':  treadViewPhotos,
          'Damage Videos':               damageVideos,
          'Rejection Reason (Claim)':    tire.claim?.rejection_reason ?? '',
          'Completion Date':             tire.claim?.completed_at ? new Date(tire.claim.completed_at).toLocaleDateString() : '',
          'Remarks':                     '',
        };
      });

      // Fetch zero-tire customers — skipped when status filter is active
      // (status is tire-specific; no-tire customers have nothing to match against it)
      let noTireRows: Record<string, string | number>[] = [];

      if (!filters.value.status) {
        try {
          const noTireCustomers = await adminCustomerService.getCustomersForExport(
            {
              search:   filters.value.search   ?? '',
              status:   '',
              brand:    filters.value.brand != null ? String(filters.value.brand) : '',
              page:     1,
              per_page: 99999,
            },
            true // noTiresOnly = true
          );

          noTireRows = noTireCustomers.map((customer: Customer) => {
            const brandName = customer.brand?.brand_name || 'Unknown';
            brandCounters[brandName] = (brandCounters[brandName] || 0) + 1;
            const rowNo = brandCounters[brandName];

            const detail = customer.userDetail ?? (customer as any).user_detail ?? null;
            const address = [detail?.house_no, detail?.street, detail?.barangay, detail?.city]
              .filter(Boolean)
              .join(', ');

            return {
              'No.':                         rowNo,
              'Category':                    customer.category === 'internal' ? 'Internal' : 'Customer',
              'Customer Code':               `${getBrandPrefix(brandName).customer}${customer.user_id}`,
              'Customer Name':               `${customer.first_name} ${customer.last_name}`.trim(),
              'Email':                       customer.email ?? '',
              'Mobile':                      detail?.mobile ?? '',
              'Brand':                       brandName,
              'Address':                     address,
              'Status (Customer)':           customer.status ?? '',
              'Vehicles Count':              customer.vehicles_count ?? 0,
              'Tires Count':                 0,
              'Date Joined':                 customer.created_at ? new Date(customer.created_at).toLocaleDateString() : '',
              'Tire Registration Code':      '',
              'Pattern':                     '',
              'Tire Size':                   '',
              'Tire Position':               '',
              'DOT':                         '',
              'Tire Photos':                 '',
              'Receipt Photos':              '',
              'Shop':                        '',
              'Code':                        '',
              'Invoice Number':              '',
              'Purchase Date':               '',
              'Vehicle':                     '',
              'Vehicle Year':                '',
              'Plate Number':                '',
              'Approval Status':             '',
              'Rejection Reason (Tire)':     '',
              'Date Submitted (Tire)':       '',
              'Claim Number':                '',
              'Dealer (Replacement)':        '',
              'Dealer Address':              '',
              'Date Submitted (Claim)':      '',
              'Status (Claim)':              '',
              'Video Call Schedule':         '',
              'Damage Photos (Whole Tire)':  '',
              'Damage Photos (Tread View)':  '',
              'Damage Videos':               '',
              'Rejection Reason (Claim)':    '',
              'Completion Date':             '',
              'Remarks':                     '',
            };
          });
        } catch (noTireErr) {
          // Non-fatal: log the error but continue with tire rows already fetched
          console.warn('Failed to fetch zero-tire customers for export:', noTireErr);
        }
      }

      // Guard: combined result must not be empty
      const allRows = [...tireRows, ...noTireRows];
      if (allRows.length === 0) {
        throw new Error('No data to export with current filters');
      }

      // Create worksheet and workbook
      const worksheet = XLSX.utils.json_to_sheet(allRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `unified_export_${timestamp}.xlsx`;

      // Trigger download
      XLSX.writeFile(workbook, filename);

      return true;
    } catch (err: any) {
      error.value = err.message || 'Failed to export tires';
      console.error('Error exporting tires:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    tires,
    selectedTire,
    loading,
    error,
    filters,
    pagination,
    pendingCount,
    approvedCount,
    rejectedCount,
    fetchTires,
    approveTire,
    rejectTire,
    updateTire,
    deleteTire,
    setFilters,
    resetFilters,
    clearError,
    exportTires
  };
});
